import { isMultiPet } from "@/shared/routing/route-resolver"
import { ROUTES, ROUTE_PREFIXES } from "@/shared/routing/routes"
import { shouldDisplayBanner } from "@/shared/utils/banner"
import { notifyPageShown } from "@/shared/utils/page-shown"
import { newSubscriptionFlowService } from "@/store/machine"
import { cn } from "@dalmatech/ui"
import { useActor } from "@xstate/react"
import { HTMLMotionProps, motion } from "framer-motion"
import { LayoutRouterContext } from "next/dist/shared/lib/app-router-context.shared-runtime"
import { usePathname } from "next/navigation"
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

import * as styles from "./page-transition.styled"

type PageTransitionProps = HTMLMotionProps<"div"> & { name: string }
type PageTransitionRef = React.ForwardedRef<HTMLDivElement>

/* 
  Nextjs's app router layout isn't properly supported, or doesn't properly support framer-motion
  This is a hack to get around that
  More infos in this thread : https://github.com/vercel/next.js/issues/49279#issuecomment-1675393849
*/
function FrozenRouter(props: PropsWithChildren<{}>) {
  const context = useContext(LayoutRouterContext)
  // eslint-disable-next-line react-hooks/refs -- intentional FrozenRouter hack: freezes the first-render router context for exit animations (see link above)
  const frozen = useRef(context).current

  return (
    <LayoutRouterContext.Provider value={frozen}>{props.children}</LayoutRouterContext.Provider>
  )
}

type ScrollToProps = { top: number; behavior?: ScrollBehavior }
const SCROLLER_ID = "scroller"
export function scrollTo({ top, behavior = "smooth" }: ScrollToProps) {
  const scroller = document.getElementById(SCROLLER_ID)
  scroller?.scrollTo({ top, behavior })
}

const AddPromoCode = ({
  setPromoCode,
}: {
  setPromoCode: Dispatch<SetStateAction<string | null | undefined>>
}): null => {
  const [state] = useActor(newSubscriptionFlowService)

  useEffect(() => {
    setPromoCode(localStorage.getItem("promoCode") || state.context.quote?.meta?.promo_code)
  }, [setPromoCode, state.context.quote?.meta?.promo_code])

  return null
}

function PageTransition({ children, ...rest }: PageTransitionProps, ref: PageTransitionRef) {
  const pathname = usePathname()
  const [promoCode, setPromoCode] = useState<string | null | undefined>(null)
  const [bannerVisible, setBannerVisible] = useState<boolean>(false)
  const [state] = useActor(newSubscriptionFlowService)
  const isWelcomePage = pathname.startsWith(ROUTE_PREFIXES.welcome)
  const isPricingPage =
    pathname.startsWith(ROUTES.pricing.path) ||
    pathname.startsWith(ROUTES.healthCoverage.path) ||
    pathname.startsWith(ROUTES.healthCoverageParam.path)
  const isBannerDisplayed = (shouldDisplayBanner() && bannerVisible) || Boolean(promoCode)
  const isRedirectIntoPrepackage = false // TODO: Définir la logique de détection si nécessaire
  const isAttributionQuestion = pathname.startsWith(ROUTES.attributionQuestion.path)
  const isPrepackage = pathname.startsWith(ROUTES.prepackages.path)
  const isRecap = pathname.startsWith(ROUTES.recap.path)
  const isTribe = pathname.startsWith(ROUTES.tribe.path)
  const isPrepackagesPage = pathname === ROUTES.prepackages.path
  const isModalitiesPage = pathname.includes(ROUTES.healthCoverageParam.path)
  const isLiabilityPage = pathname.includes(ROUTES.liabilityAddOn.path)
  const isCoveragePage = pathname.includes(ROUTES.healthCoverage.path)

  const isMultiPetPrepackageBanner =
    (isPrepackagesPage || isLiabilityPage || isModalitiesPage || isCoveragePage) && isMultiPet(state.context.quote)
  const transparent = { opacity: 0 }
  const visible = { opacity: 1, transition: { delay: 0.15 } }

  const transition = {
    duration: 0.15,
    ease: "easeOut" as const,
  }

  useEffect(() => {
    const bannerClosed = localStorage.getItem("bannerClosed") === "true"
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot hydration on mount from localStorage; setState-in-effect is the intended pattern here
    setBannerVisible(!bannerClosed)
  }, [])

  // One PageTransition mounts per pathname (AnimatePresence mode="wait"), so
  // this fires when the new page actually shows up — not when the URL commits.
  useEffect(() => {
    notifyPageShown()
  }, [])

  return (
    <motion.main
      ref={ref}
      initial={transparent}
      animate={visible}
      exit={transparent}
      transition={transition}
      className={cn(styles.root, rest.className)}
      {...rest}
    >
      <motion.div className={cn(styles.scroller)} layout id={SCROLLER_ID}>
        <FrozenRouter>
          {isWelcomePage ? null : <AddPromoCode setPromoCode={setPromoCode} />}
          <motion.div
            className={cn(
              styles.container(
                isPricingPage,
                isBannerDisplayed,
                isRedirectIntoPrepackage,
                isAttributionQuestion,
                isPrepackage,
                isRecap,
                isTribe,
                isMultiPetPrepackageBanner,
                isWelcomePage
              )
            )}
            layout
          >
            {children}
          </motion.div>
        </FrozenRouter>
      </motion.div>
    </motion.main>
  )
}

export default forwardRef(PageTransition)
